import { useRef, useState, useMemo, ComponentPropsWithoutRef } from 'react';
import styled from 'styled-components';

import TreeNode from 'assets/TreeNode';
import TreeRootNode from 'assets/TreeRootNode';
import TreeSiblingLine from 'assets/TreeSiblingLine';

import colors, { opacity } from 'styles/colors';
import ChevronIcon from 'assets/ChevronIcon';
import ListIcon from 'assets/ListIcon';
import { fontWeight } from 'styles/common';

const DEFAULT_EMPTY_LABEL = '---';

const PopoverItem = styled.div`
  padding: 0 8px 0 4px;
  overflow-x: hidden;
  text-wrap: nowrap;
  text-overflow: ellipsis;
`;

const ROOT_NODE_CLASSNAME = 'is-root-node';

const CHANGE_ALL_SVG_CHILDREN_STROKE = `svg *[stroke] { stroke: ${colors.blue}; }`;
const CHANGE_RECT_SVG_CHILDREN_STROKE = `svg rect { stroke: ${colors.blue}; }`;

const TreeItem = styled.div<{
  $selected?: boolean;
  $isRootNode: boolean;
  $nodeClassname: string;
  $highlightRootChildrenNodes: boolean;
}>`
  font-size: 12px;
  overflow: clip;
  display: flex;
  align-items: center;
  cursor: pointer;
  ${({ $selected }) =>
    $selected &&
    `
    background-color: ${opacity(colors.blue, 0.1)};
    & ${CHANGE_RECT_SVG_CHILDREN_STROKE} }
    `}

  ${({ $isRootNode, $selected, $highlightRootChildrenNodes }) =>
    $isRootNode &&
    $selected &&
    $highlightRootChildrenNodes &&
    `
    & ${CHANGE_ALL_SVG_CHILDREN_STROKE}
  `}

  & svg * {
    transition: stroke 0.15s ease;
  }

  &:hover {
    background-color: ${({ $selected }) => ($selected ? opacity(colors.blue, 0.2) : opacity(colors.blue, 0.1))};
    ${({ $isRootNode, $highlightRootChildrenNodes }) =>
      $isRootNode && $highlightRootChildrenNodes
        ? `& ${CHANGE_ALL_SVG_CHILDREN_STROKE}`
        : `& ${CHANGE_RECT_SVG_CHILDREN_STROKE}`}
  }

  ${({ $selected, $nodeClassname, $highlightRootChildrenNodes }) => {
    // Root tree trace styling, applies effect to the root node's children when the item is selected
    return (
      $highlightRootChildrenNodes &&
      $selected &&
      `
      &.${$nodeClassname.split(' ')[0]}.${ROOT_NODE_CLASSNAME} ~ &.${$nodeClassname.split(' ')[0]} {
        & ${CHANGE_ALL_SVG_CHILDREN_STROKE}
      }
    `
    );
  }}

  ${({ $nodeClassname, $highlightRootChildrenNodes }) => {
    // Root tree trace styling, applies svg hover effect to the root node's children
    return (
      $highlightRootChildrenNodes &&
      `
      &:hover.${$nodeClassname.split(' ')[0]}.${ROOT_NODE_CLASSNAME} ~ &.${$nodeClassname.split(' ')[0]} {
        & ${CHANGE_ALL_SVG_CHILDREN_STROKE}
      }
    `
    );
  }}
`;

const Select = styled.div`
  font-family: 'Open Sans';
  font-weight: ${fontWeight.semibold};
  display: flex;
  flex-grow: 1;
  align-items: start;
  flex-direction: column;
  position: relative;
  & svg {
    flex-shrink: 0;
  }

  & > button {
    display: flex;
    font-family: 'Open Sans';
    font-weight: ${fontWeight.semibold};
    padding: 3px 11px;
    width: 100%;
    font-size: 14px;
    line-height: 14px;
    border: 1px solid ${colors.fleetOffGray};
    border-radius: 4px;
    background-color: ${colors.white};
    cursor: pointer;

    & > div {
      display: flex;
      align-items: center;
      height: 100%;
      gap: 8px;
      justify-content: space-between;
      width: 100%;
    }

    &:hover {
      background-color: ${opacity(colors.fleetOffGray, 0.2)};
    }

    &:has(+ div[popover='auto']:popover-open) {
      border-radius: 4px 4px 0 0;

      & > div > svg:nth-last-of-type(1) {
        transform: rotate(180deg);
      }
    }
  }

  @position-try --custom-bottom {
    position-area: bottom;
    margin-top: -1px;
  }

  @position-try --custom-top {
    position-area: top;
    margin-bottom: -1px;
  }

  & [popover] {
    padding: 0;
    margin: 0;
    box-sizing: border-box;
    width: anchor-size(width);
    border-radius: 0 0 4px 4px;
    overflow-x: auto;
    border: 1px solid ${colors.fleetOffGray};

    position-area: bottom;
    margin-top: -1px;
    justify-self: anchor-center;
    position-try-fallbacks: --custom-top, --custom-bottom;
  }
`;

export type OptionValue = string | number;

export interface Option<TValue extends OptionValue = OptionValue> {
  value: TValue;
  label: string;
  parent?: TValue;
  onAncestorLevel?: boolean;
}

interface TreeSelectProps<TOptions extends Option[] = Option[]> extends ComponentPropsWithoutRef<'div'> {
  emptyLabel?: string;
  highlightRootChildrenNodes?: boolean;
  initialValue?: TOptions[number]['value'];
  onValueChange?: (value: TOptions[number]['value']) => void;
  onChange?: never;
  options: Option[];
}

type OptionWithMeta<TValue extends OptionValue = OptionValue> = Option<TValue> & {
  depth: number;
  hasNextSibling: boolean;
  hasChildren: boolean;
  ancestorsHadSibbling: boolean[];
  childrenCount: number;
  className: string;
};

const getOptionClassName = (value: string | number): string => {
  return typeof value === 'string' ? `node-${value}` : `node-${value.toString()}`;
};

/**
 * Recursively orders a flat list of options into a depth-first tree structure,
 * enriching each option with metadata needed for tree rendering.
 *
 * @param options - The full flat list of options to be ordered.
 * @param depth - The current depth level in the tree (default: 0 for root).
 * @param parent - The value of the parent option whose children are being
 *   processed. `null` indicates root-level options (default: null).
 * @param ancestorsHadSibbling - A set of depth levels at which an ancestor has a
 *   following sibling. Used to render vertical continuation lines (│) in the
 *   tree UI (default: empty Set).
 * @param alreadyOrdered - A list of option values that have already been
 *   placed in the output, used to avoid duplicating root-level options that
 *   have no explicit parent (default: []).
 *
 * @returns A flat array of `OptionWithMeta` in depth-first order, where each
 *   entry is annotated with:
 *   - `depth`: how deep in the tree the option sits
 *   - `hasNextSibling`: whether the option has a following sibling
 *   - `hasChildren`: whether the option has any child options
 *   - `relatedSiblingsDepths`: the set of ancestor depths that still have
 *     remaining siblings, for drawing tree branch lines
 */
const orderByParent = (
  options: Option[],
  depth = 0,
  parent: OptionValue = null,
  ancestorsHadSibbling: boolean[] = [],
  alreadyOrdered: OptionValue[] = [],
  parentClassName = ''
): OptionWithMeta[] => {
  const ordered: OptionWithMeta[] = [];

  const rootNodes = options.filter(
    (option) => option.parent === parent || (option.parent === undefined && !alreadyOrdered.includes(option.value))
  );

  const rootNodesValues = rootNodes.map((option) => option.value);

  for (const option of rootNodes) {
    const hasNextSibling = rootNodes.length > rootNodes.findIndex((o) => o.value === option.value) + 1;
    const hasChildren = options.some((o) => o.parent === option.value);
    const className = parentClassName
      ? `${parentClassName} ${getOptionClassName(option.value)}`
      : getOptionClassName(option.value);

    const childrenAncestorsHadSibbling = [...ancestorsHadSibbling];
    childrenAncestorsHadSibbling.push(hasNextSibling);
    const children = orderByParent(
      options,
      depth + 1,
      option.value,
      childrenAncestorsHadSibbling,
      [...alreadyOrdered, ...rootNodesValues],
      className
    );
    ordered.push(
      {
        ...option,
        depth: depth,
        hasNextSibling,
        hasChildren,
        ancestorsHadSibbling,
        className: className,
        childrenCount: children.length,
      },
      ...children
    );
  }

  return ordered;
};

const SiblingTraces = ({
  depth,
  ancestorsHadSibbling,
}: { depth: number; ancestorsHadSibbling?: boolean[] } | { depth: -1; ancestorsHadSibbling?: never }) => {
  const depthInfo = [];

  if (depth >= 0) {
    for (let i = 1; i < depth; i++) {
      depthInfo.push(ancestorsHadSibbling[i] ? <TreeSiblingLine /> : <div style={{ width: 15 }} />);
    }
  }

  return <div style={{ display: 'flex' }}>{depthInfo}</div>;
};

const TreeIcon = ({
  hasParent,
  hasNextSibling,
  hasChildren,
  onAncestorLevel = false,
}: {
  hasParent: boolean;
  hasNextSibling: boolean;
  hasChildren: boolean;
  onAncestorLevel?: boolean;
}) => {
  if (!hasParent) {
    return <TreeRootNode hasChildren={hasChildren} />;
  } else if (onAncestorLevel) {
    return <TreeRootNode hasChildren hasParent />;
  } else {
    return <TreeNode hasChildren={hasChildren} hasSibling={hasNextSibling} />;
  }
};

const TreeSelectOption = ({
  option,
  selected,
  highlightRootChildrenNodes = true,
  ...props
}: React.ComponentPropsWithoutRef<'div'> & {
  option: OptionWithMeta;
  selected?: boolean;
  highlightRootChildrenNodes?: boolean;
}) => {
  const hasParent = Boolean(option.parent);
  return (
    <TreeItem
      className={!hasParent ? option.className + ' ' + ROOT_NODE_CLASSNAME : option.className}
      {...props}
      $highlightRootChildrenNodes={highlightRootChildrenNodes}
      $selected={selected}
      $isRootNode={!hasParent}
      $nodeClassname={option.className}
      style={{
        paddingLeft: hasParent && !option.onAncestorLevel ? 11 : 0,
      }}
    >
      {option.onAncestorLevel ? (
        <SiblingTraces depth={-1} />
      ) : (
        <SiblingTraces depth={option.depth} ancestorsHadSibbling={option.ancestorsHadSibbling} />
      )}

      <TreeIcon
        hasParent={Boolean(option.parent)}
        hasNextSibling={option.hasNextSibling}
        hasChildren={option.hasChildren}
        onAncestorLevel={option.onAncestorLevel}
      />
      <PopoverItem>{option.label}</PopoverItem>
    </TreeItem>
  );
};

const TreeSelect = ({
  onValueChange,
  options,
  emptyLabel = DEFAULT_EMPTY_LABEL,
  initialValue,
  highlightRootChildrenNodes = true,
  ...props
}: TreeSelectProps) => {
  const popoverID = useMemo(() => Math.random().toString(36).substring(2, 9), []);
  const [selectValue, setSelectValue] = useState(options.find((option) => option.value === initialValue)?.value);
  const popoverRef = useRef<HTMLDivElement>(null);

  const hidePopover = () => {
    if (popoverRef.current) {
      popoverRef.current.hidePopover();
    }
  };

  const handleSelect = (value: OptionValue) => {
    setSelectValue(value);
    if (onValueChange) onValueChange(value);

    hidePopover();
  };

  const orderedOptions = orderByParent(options);

  return (
    <Select {...props}>
      {/* @ts-expect-error `popovertarget` does not exist within current DOM typing lib */}
      <button type="button" popovertarget={popoverID}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ListIcon color={colors.black} />
            {options.find((option) => option.value === selectValue)?.label ?? emptyLabel}
          </div>
          <ChevronIcon color={colors.black} />
        </div>
      </button>
      {/* @ts-expect-error `popover` does not exist within current DOM typing lib */}
      <div ref={popoverRef} id={popoverID} popover="auto">
        {orderedOptions.map((option) => {
          return (
            <TreeSelectOption
              key={option.value}
              onClick={() => handleSelect(option.value)}
              option={option}
              highlightRootChildrenNodes={highlightRootChildrenNodes}
              selected={option.value === selectValue}
            />
          );
        })}
      </div>
    </Select>
  );
};

export default TreeSelect;
